import { ApolloQueryResult } from '@apollo/client';
import { ClientEvents } from 'common/events/ClientEvents';
import { SessionStorageKeys } from 'common/storage/constants';
import { first, forEach, isEmpty, isEqual, last, map, size, sum } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  ConsumerExploreDishFirstQuery,
  ExpectedPaginationInfoInput,
  useConsumerExploreDishFirstQuery,
} from 'src/gqlReactTypings.generated.d';
import { mergePaginatedSegments } from 'src/pages/consumer/dish-first/explore/mergePaginatedSegments';
import { UseDishFirstExploreUrl } from 'src/pages/consumer/explore/useExploreUrl';
import { SpiceLevelMap } from 'src/shared/Enums';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useSessionStorage } from 'src/shared/storage/Storage';
import { match } from 'ts-pattern';
import { v4 as uuid } from 'uuid';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ScreenWidth } from 'web-common/src/shared/styles';
import { getSession } from '../../../../../../tracking/tracking';
import { useDishFirstExploreFilters } from '../useDishFirstExploreFilters';

const REGULAR_PAGE_SIZE = 30;

interface IConsumerExploreCache {
  [key: string]: ConsumerExploreDishFirstQuery | undefined;
}

export const useCachedDishFirstExplore = (
  {
    zipCode,
    browseFilter,
    dateFilters: availabilityDates,
    dietaryFilters,
    dishTypeFilters,
    spiceLevelFilters,
    tagFilters,
    proteinFilters,
    minRatingFilter: minDishRating,
    segmentFilters,
    cuisineFilters,
    ecoFilter: isEcoFriendlyPackaging,
    priceLevelFilters,
  }: UseDishFirstExploreUrl,
  availableDeliveryDays: string[]
) => {
  const tracker = useTracker();
  const dispatch = useDispatch();
  const [cache, setCache] = useSessionStorage<IConsumerExploreCache>(
    SessionStorageKeys.DISH_FIRST_EXPLORE_STORAGE_KEY,
    {}
  );
  const [moreSegmentsExist, setMoreSegmentsExist] = useState(true);
  const [isMoreLoading, setIsMoreLoading] = useState(false);
  // We use this to ignore the results of async fetchMore calls if
  // they're out of date:
  const requestCounter = useRef(0);

  const { filters, stringifiedFilters } = useDishFirstExploreFilters({
    availabilityDates,
    browseFilter,
    dietaryFilters,
    dishTypeFilters,
    spiceLevelFilters,
    proteinFilters,
    minDishRating,
    segmentFilters,
    cuisineFilters,
    isEcoFriendlyPackaging,
    priceLevelFilters,
    tagFilters,
  });

  const trackSegmentRender = useCallback(
    (segment: ConsumerExploreDishFirstQuery['exploreFoodItems'][number], pos: number) => {
      tracker.track(ClientEvents.EXPLORE_FOOD_ITEMS_RENDERED, {
        pos,
        segmentFilters: filters.segmentFilters ?? undefined,
        esid: segment.id,
        layout: segment.layout,
        itemIds: map(segment.content.items, getIdFromItem),
        total: segment.content.total,
        isDishSegment: segment.content.items[0].__typename === 'FoodItem',
        deliveryDay: filters.availabilityDates[0],
      });
    },
    [tracker, filters]
  );

  useEffect(() => {
    setMoreSegmentsExist(true);
    requestCounter.current += 1;
  }, [stringifiedFilters]);

  const cachedDataRaw = useMemo(() => cache[stringifiedFilters], [cache, stringifiedFilters]);
  // If a segment gets split across pages, multiple segments with its same ID will appear adjacent to
  // each other, with different contents in each such adjacent segment.
  // We want to merge those back into a single segment:
  const cachedDataMerged: ConsumerExploreDishFirstQuery | undefined = useMemo(
    () => mergePaginatedSegments(cachedDataRaw),
    [cachedDataRaw]
  );

  // Keep track and return the last used cache data. That way, when new requests
  // come in, we only swap the data when the new data is populated.
  const [lastUsedCacheData, setLastUsedCachedData] = useState(cachedDataMerged);
  useEffect(() => {
    if (cachedDataMerged) {
      setLastUsedCachedData(cachedDataMerged);
    }
  }, [cachedDataMerged]);

  const seed = useMemo(() => getSession()?.id ?? uuid(), []);
  const { nearestScreenWidth } = useScreenContext();
  const numVisiblePerSegment = useMemo(
    () =>
      match(nearestScreenWidth)
        .with(ScreenWidth.EXTRA_EXTRA_SMALL, ScreenWidth.EXTRA_SMALL, () => 2)
        .with(ScreenWidth.SMALL, ScreenWidth.MEDIUM, ScreenWidth.LARGE, () => 3)
        .otherwise(() => 4),
    [nearestScreenWidth]
  );

  const isViewingSegment = !isEmpty(filters.segmentFilters) || !isEmpty(filters.cuisineFilters);

  const initialVariables = useMemo(
    () => ({
      zipCode: zipCode ?? '',
      offset: 0,
      limit: REGULAR_PAGE_SIZE,
      seed,
      numVisiblePerSegment,
      filters: {
        ...filters,
        spiceLevelFilters: spiceLevelFilters.map((value) => SpiceLevelMap[value]),
      },
      numShefFoodItems: 1,
      loadFoodItemDescription: isViewingSegment,
      useDynamicPreviewImage: true,
      availableDeliveryDays,
      tagFilters,
    }),
    [filters, numVisiblePerSegment, seed, spiceLevelFilters, zipCode, isViewingSegment]
  );

  const { data, error, loading, variables, fetchMore } = useConsumerExploreDishFirstQuery({
    variables: initialVariables,
    returnPartialData: true,
    fetchPolicy: 'cache-first',
  });

  // Pre-calculate the offset we're currently at in the pagination.
  // (Note that we must use data for this, not cachedData, because
  // when we call fetchMore based on this offset, Apollo will append
  // the results to data, which will then become the new cachedData.
  // If we calculated the offset based off of cachedData, we would have
  // the wrong offset if cachedData is stale compared to data.)
  const rawDataMerged = useMemo(() => mergePaginatedSegments(data), [data]);

  const currentOffset = useMemo(() => {
    const segmentsMerged = rawDataMerged?.exploreFoodItems ?? [];

    return segmentsMerged.reduce(
      (segmentCountSoFar: number, thisSegment) => segmentCountSoFar + thisSegment.content.items.length,
      0
    );
  }, [rawDataMerged]);

  useEffect(() => {
    const dataIsUseful = data !== null && typeof data === 'object' && Object.keys(data).length > 0;
    const isResponseDifferent = !isEqual(data, cachedDataRaw);
    if (!loading && variables?.filters && data && isResponseDifferent && dataIsUseful) {
      setCache({ [stringifiedFilters]: data });
    }
  }, [cache, cachedDataRaw, loading, data, dispatch, setCache, stringifiedFilters, variables]);

  const fetchNextPage = useCallback(
    async ({ showLoader } = { showLoader: true }) => {
      if (!loading && currentOffset > 0 && moreSegmentsExist) {
        requestCounter.current += 1;
        const requestCounterWhenRequestStarted = requestCounter.current;
        try {
          if (showLoader) {
            setIsMoreLoading(true);
          }
          const results: ApolloQueryResult<ConsumerExploreDishFirstQuery> = await fetchMore({
            variables: {
              offset: currentOffset,
              limit: REGULAR_PAGE_SIZE,
              expectedPaginationInfo: getExpectedPaginationInfo(rawDataMerged),
              availableDeliveryDays,
            },
          });
          if (requestCounterWhenRequestStarted !== requestCounter.current) {
            setIsMoreLoading(false);
            // These are stale results. Don't do anything with them.
            return;
          }
          const exploreItems = results?.data?.exploreFoodItems;
          const resultsLength = sum(exploreItems.map((segment) => segment?.content?.items?.length ?? 0));
          const currentNumSegments = size(rawDataMerged?.exploreFoodItems);
          const isSplitSegment = last(rawDataMerged?.exploreFoodItems)?.id === first(exploreItems)?.id;
          forEach(exploreItems, (segment, i) =>
            trackSegmentRender(segment, i + currentNumSegments - (isSplitSegment ? 1 : 0))
          );

          setMoreSegmentsExist(resultsLength === REGULAR_PAGE_SIZE);
          setIsMoreLoading(false);
        } catch (e) {
          setIsMoreLoading(false);
          console.error(e);
        }
        return Promise.resolve(null);
      }
      return Promise.resolve(null);
    },
    [trackSegmentRender, loading, fetchMore, currentOffset, rawDataMerged, moreSegmentsExist]
  );

  // Immediately start loading a 2nd page before the user even scrolls:
  useEffect(() => {
    const isOffsetFirstPage = currentOffset > 0 && currentOffset <= REGULAR_PAGE_SIZE;
    const isDoneWithFirstPage = isOffsetFirstPage && !loading;
    if (isDoneWithFirstPage) forEach(data?.exploreFoodItems, trackSegmentRender);
    if (isDoneWithFirstPage && moreSegmentsExist) fetchNextPage({ showLoader: false }).catch(console.error);
  }, [trackSegmentRender, data?.exploreFoodItems, currentOffset, fetchNextPage, moreSegmentsExist, loading]);

  return {
    data: lastUsedCacheData,
    moreSegmentsExist,
    error,
    loading,
    fetchNextPage,
    isMoreLoading: !loading && isMoreLoading,
  };
};

type ExploreItem = ConsumerExploreDishFirstQuery['exploreFoodItems'][number]['content']['items'][number];
const getIdFromItem = (item: ExploreItem): string =>
  match(item)
    .with({ __typename: 'FoodItem' }, (foodItem) => foodItem.foodItemId.toString())
    .with({ __typename: 'ExploreSegmentShef' }, (segment) => segment.shef.id)
    .otherwise(() => 'ERROR this should not be possible');

const getExpectedPaginationInfo = (
  exploreData: ConsumerExploreDishFirstQuery | undefined
): ExpectedPaginationInfoInput | undefined => {
  if (!exploreData || exploreData.exploreFoodItems.length === 0) {
    return undefined;
  }
  const finalSegmentOfCurrentPage = exploreData.exploreFoodItems[exploreData.exploreFoodItems.length - 1];
  const finalItemOfFinalSegment =
    finalSegmentOfCurrentPage.content.items[finalSegmentOfCurrentPage.content.items.length - 1];
  return {
    expectedPreviousPageFinalSegmentId: finalSegmentOfCurrentPage.id,
    expectedPreviousPageFinalItemId: getIdFromItem(finalItemOfFinalSegment),
  };
};
